/** SHED & OUTBUILDING PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/f041081b-8771-4f5e-b50b-9d475076e4ef/`

export const PRODUCT = {
  name: 'SHED & OUTBUILDING PLANS™',
  wordmarkA: 'Shed & Outbuilding',
  wordmarkB: 'Plans',
  descriptor: 'Shed & Outbuilding Plans to Build & Sell',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build them for your own backyard — or build and sell the finished buildings to people near you.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 BACKYARD BUILDINGS',
  '6 DIGITAL VOLUMES',
  'FRAMING PLANS & CUT LISTS',
  'LUMBER-YARD SHOPPING LISTS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages are vertical 4:5. */
const PAGE_SIZE = { width: 1100, height: 1376 }

const COVERS = {
  v1: `${IMG}1792827963292_sh-vol-1-d769d4ad.webp`,
  v2: `${IMG}1792827969118_sh-vol-2-f8943a45.webp`,
  v3: `${IMG}1792827974946_sh-vol-3-2934ded7.webp`,
  v4: `${IMG}1792827980861_sh-vol-4-46d786f3.webp`,
  v5: `${IMG}1792827986865_sh-vol-5-b6a40805.webp`,
  v6: `${IMG}1792827992805_sh-vol-6-659acdb1.webp`,
}

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792827998341_sh-hero-mockup-f73996c7.webp`,
  alt: 'The six SHED & OUTBUILDING PLANS™ volume covers: garden sheds, barn-style sheds, she sheds and offices, coops and animal shelters, yard buildings, pavilions and carports',
  width: 1100,
  height: 1375,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Backyard Buildings', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Framing Plans + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P01, Volume 01, PDF pages 06–12 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P01',
  name: '6 x 8 FT Garden Tool Shed',
  volume: 'Volume 01',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  { src: `${IMG}1792828003755_sh-p01-01-finished-4aa0ac84.webp`, caption: 'The finished build', alt: 'Volume 01, page 06: P01 6 x 8 FT Garden Tool Shed finished build — 72 x 96 in / 1828.8 x 2438.4 mm footprint', ...PAGE_SIZE },
  { src: `${IMG}1792828009092_sh-p01-02-framing-8633ba5f.webp`, caption: 'Framing plan — floor, walls & roof', alt: 'Volume 01, page 07: P01 framing plan with dimensions in inches and millimeters', ...PAGE_SIZE },
  { src: `${IMG}1792828014929_sh-p01-03-cutlist-ae793eed.webp`, caption: 'Exploded view & complete cut list', alt: 'Volume 01, page 08: P01 exploded view and cut list', ...PAGE_SIZE },
  { src: `${IMG}1792828020677_sh-p01-04-steps-1a7df956.webp`, caption: 'Step-by-step build', alt: 'Volume 01, page 09: P01 illustrated build sequence', ...PAGE_SIZE },
  { src: `${IMG}1792828026945_sh-p01-05-details-fcbe4e32.webp`, caption: 'Framing & roof details', alt: 'Volume 01, page 10: P01 framing and roof details', ...PAGE_SIZE },
  { src: `${IMG}1792828032101_sh-p01-06-shopping-4222af3f.webp`, caption: 'Lumber-yard shopping list', alt: 'Volume 01, page 11: P01 lumber-yard shopping list', ...PAGE_SIZE },
  { src: `${IMG}1792828037725_sh-p01-07-sell-card-6f91929f.webp`, caption: 'Build & Sell card', alt: 'Volume 01, page 12: P01 Build & Sell card', ...PAGE_SIZE },
]

export const PLAN_BENEFITS: string[] = ['Buy the right lumber.', 'Frame it square.', 'Use it — or sell it.']

export const TECH_PROOF: TechProof[] = [
  { src: `${IMG}1792828043143_sh-tech-01-p02-framing-3929459d.webp`, title: 'KNOW THE FRAMING', project: 'Project 02 · 8 x 8 FT Gable Shed · Volume 01', description: 'Floor, walls and roof framed and dimensioned before you cut.', alt: 'Volume 01: P02 8 x 8 FT Gable Shed framing plan', ...PAGE_SIZE },
  { src: `${IMG}1792828048763_sh-tech-02-p03-cutlist-44d3507d.webp`, title: 'KNOW WHAT TO CUT', project: 'Project 03 · 8 x 10 FT Gable Shed with Double Doors · Volume 01', description: 'Parts, quantities, lumber and exact cut sizes in one table.', alt: 'Volume 01: P03 exploded view and complete cut list', ...PAGE_SIZE },
  { src: `${IMG}1792828054219_sh-tech-03-p04-steps-bb8192e8.webp`, title: 'FOLLOW THE BUILD', project: 'Project 04 · 10 x 12 FT Gable Shed with Windows · Volume 01', description: 'An illustrated sequence from the floor to the finished roof.', alt: 'Volume 01: P04 step-by-step build', ...PAGE_SIZE },
  { src: `${IMG}1792828059599_sh-tech-04-p05-details-546d285b.webp`, title: 'GET THE DETAILS RIGHT', project: 'Project 05 · 8 x 12 FT Lean-To Shed · Volume 01', description: 'Floor connections, door headers and rafter seats, up close.', alt: 'Volume 01: P05 framing and roof details', ...PAGE_SIZE },
]

const COVER_SIZE = { width: 1100, height: 1376 }

export const VOLUMES: Volume[] = [
  { number: 'VOLUME 01', title: 'Garden Storage Sheds', projects: '5 projects', description: 'The classic backyard sheds, from 6 x 8 to 10 x 12 ft.', projectList: ['P01 — 6 x 8 FT Garden Tool Shed', 'P02 — 8 x 8 FT Gable Shed', 'P03 — 8 x 10 FT Gable Shed with Double Doors', 'P04 — 10 x 12 FT Gable Shed with Windows', 'P05 — 8 x 12 FT Lean-To Shed'], src: COVERS.v1, alt: 'Volume 01 cover: Garden Storage Sheds', ...COVER_SIZE },
  { number: 'VOLUME 02', title: 'Large & Barn-Style Sheds', projects: '5 projects', description: 'Gambrel barns, a workshop and garage-style storage.', projectList: ['P06 — 12 x 16 FT Gambrel Barn Shed with Loft', 'P07 — 10 x 16 FT Gable Workshop Shed', 'P08 — 12 x 20 FT Garage-Style Storage Building', 'P09 — 12 x 12 FT Gambrel Barn Shed', 'P10 — 10 x 20 FT Lean-To Storage Building'], src: COVERS.v2, alt: 'Volume 02 cover: Large & Barn-Style Sheds', ...COVER_SIZE },
  { number: 'VOLUME 03', title: 'She Sheds, Offices & Cabins', projects: '5 projects', description: 'Backyard rooms to work, create or stay.', projectList: ['P11 — 8 x 12 FT She Shed with Porch', 'P12 — 10 x 12 FT Backyard Office with Clerestory', 'P13 — 12 x 16 FT Cabin Shell with Front Porch', 'P14 — 10 x 10 FT Modern Studio Shed', 'P15 — 12 x 14 FT Bunkhouse'], src: COVERS.v3, alt: 'Volume 03 cover: She Sheds, Offices & Cabins', ...COVER_SIZE },
  { number: 'VOLUME 04', title: 'Chicken Coops & Animal Shelters', projects: '5 projects', description: 'Coops, a goat shelter and a rabbit hutch.', projectList: ['P16 — 4 x 6 FT Chicken Coop with Run', 'P17 — 6 x 8 FT Walk-In Chicken Coop', 'P18 — 8 x 10 FT Coop with Covered Run', 'P19 — 8 x 12 FT Three-Sided Goat & Sheep Shelter', 'P20 — Two-Tier Rabbit Hutch'], src: COVERS.v4, alt: 'Volume 04 cover: Chicken Coops & Animal Shelters', ...COVER_SIZE },
  { number: 'VOLUME 05', title: 'Small Storage & Yard Buildings', projects: '5 projects', description: 'Firewood, trash cans, potting and mower storage.', projectList: ['P21 — 4 x 8 FT Firewood Shed', 'P22 — Three-Bin Trash Can Enclosure', 'P23 — 6 x 8 FT Potting Shed with Bench', 'P24 — 5 x 7 FT Lawn Mower Shed with Ramp', 'P25 — 3 x 6 FT Lean-To Tool Shed'], src: COVERS.v5, alt: 'Volume 05 cover: Small Storage & Yard Buildings', ...COVER_SIZE },
  { number: 'VOLUME 06', title: 'Pavilions & Carports', projects: '5 projects', description: 'Open-air timber structures for the patio and the driveway.', projectList: ['P26 — 12 x 12 FT Backyard Pavilion', 'P27 — 10 x 20 FT Single Carport', 'P28 — 20 x 20 FT Double Carport', 'P29 — 10 x 10 FT Hot Tub Pavilion', 'P30 — 8 x 12 FT Lean-To Carport for ATV or Boat'], src: COVERS.v6, alt: 'Volume 06 cover: Pavilions & Carports', ...COVER_SIZE },
]

const BONUS_SIZE = { width: 1100, height: 1100 }

export const BONUSES: Bonus[] = [
  { number: '01', title: 'Lumber Takeoff & Cut List Planner', description: 'Buy the right boards and sheets once, then cut them in order.', src: `${IMG}1792828065222_sh-bonus-01-d26b2f02.webp`, alt: 'Bonus 01: Lumber Takeoff & Cut List Planner', ...BONUS_SIZE },
  { number: '02', title: 'Site Planning & Permit Checklist', description: 'Pick the spot, check setbacks and plan the foundation.', src: `${IMG}1792828070756_sh-bonus-02-710fc15d.webp`, alt: 'Bonus 02: Site Planning & Permit Checklist', ...BONUS_SIZE },
  { number: '03', title: 'Roof Pitch & Rafter Layout Guide', description: 'Lay out rafters and birdsmouth cuts without guesswork.', src: `${IMG}1792828076137_sh-bonus-03-8e9b4399.webp`, alt: 'Bonus 03: Roof Pitch & Rafter Layout Guide', ...BONUS_SIZE },
  { number: '04', title: 'Costing & Pricing Workbook', description: 'Know what each building really costs before you set a price.', src: `${IMG}1792828081563_sh-bonus-04-fb678da5.webp`, alt: 'Bonus 04: Costing & Pricing Workbook', ...BONUS_SIZE },
  { number: '05', title: 'Local Shed Selling Kit', description: 'Listing templates, photo setups and simple quotes for shed buyers.', src: `${IMG}1792828086863_sh-bonus-05-4944bc74.webp`, alt: 'Bonus 05: Local Shed Selling Kit', ...BONUS_SIZE },
]

export const OFFER_FEATURES: string[] = [
  'Framing plans with every measurement in inches and millimeters',
  'Exploded views, complete cut lists and step-by-step builds',
  'Lumber-yard shopping lists and a Build & Sell card for every project',
]

export const FAQS: Faq[] = [
  { question: 'What am I buying?', answer: 'A digital collection of shed and outbuilding plans: 30 backyard buildings across six PDF volumes, plus five bonus resources.' },
  { question: 'Who is this collection for?', answer: 'DIY builders, handymen and carpenters who can use a saw and a drill and want clear plans for sheds, coops, pavilions and carports — for their own backyard or to build and sell.' },
  { question: 'Do I need to be an experienced carpenter?', answer: 'You should be comfortable with basic tools. Every project shows the framing, the cut list and each step in order, so you always know what comes next.' },
  { question: 'What kinds of buildings are included?', answer: 'Garden sheds, gambrel and barn-style sheds, a workshop, she sheds and backyard offices, a cabin shell, chicken coops, a goat shelter, a rabbit hutch, firewood and trash can storage, pavilions and carports.' },
  { question: 'Are the plans in inches or millimeters?', answer: 'Both. Every dimension on every project page is printed in inches and millimeters. Lumber keeps its nominal names (2x4, 4x4).' },
  { question: 'Are these physical books or kits?', answer: 'The product is digital. You receive PDF files by email right after payment; lumber, materials and tools are not included. Check your local permit and setback rules before you build.' },
  { question: 'Can I preview the plans?', answer: 'Yes. All seven pages of Project P01 — 6 x 8 FT Garden Tool Shed, from Volume 01, are in the preview section above.' },
  { question: 'Can I sell the buildings I make from these plans?', answer: 'Yes. Your purchase includes permission to build projects from these plans for your own use or to sell the physical buildings you make. You may print working copies for your own use. You may not share, resell, redistribute or sublicense the digital plans, PDFs, images or bonus files.' },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
